/**
 * Prospect Stage Cache Service
 * Fetches stage definitions from the backend API and caches them per US state.
 * Single source of truth — no hardcoded fallback. Stages are empty until API responds.
 *
 * State-aware: CA stages are preloaded on auth (always-needed default). Other states
 * (e.g. CO) are fetched on demand when the user opens a transaction whose licence state
 * differs from CA. Unknown / missing state always resolves to CA so the timeline is
 * never empty.
 *
 * Mirrors mobile's prospectStageCache.service.ts exactly.
 */

import { apiClient, API_ENDPOINTS } from '../config/api';

const DEFAULT_STATE = 'CA';

// state code → { BUYING: [...], LISTING: [...] }
const cache = new Map();

// Per-state in-flight promises — dedup concurrent fetches for the same state
const loadPromises = new Map();

const normalizeState = (state) => {
  if (!state) return DEFAULT_STATE;
  const upper = String(state).trim().toUpperCase();
  if (!upper) return DEFAULT_STATE;
  // Handle ISO 3166-2 format (e.g. "US-CA" → "CA")
  if (upper.length === 5 && upper.startsWith('US-')) return upper.slice(3);
  return upper;
};

/**
 * Fetch stage definitions for a given state from the backend API. Defaults to CA.
 * Safe to call multiple times — concurrent calls for the same state are deduped and
 * a state is only refetched when force === true.
 *
 * Backward-compat: callers that used loadProspectStages(true) to force a CA reload
 * still work — a boolean first arg is treated as the force flag for the CA state.
 */
export const loadProspectStages = (stateOrForce, forceArg = false) => {
  let state;
  let force;
  if (typeof stateOrForce === 'boolean') {
    state = DEFAULT_STATE;
    force = stateOrForce;
  } else {
    state = normalizeState(stateOrForce);
    force = forceArg;
  }

  if (cache.has(state) && !force) {
    return Promise.resolve();
  }

  const existing = loadPromises.get(state);
  if (existing) return existing;

  const promise = (async () => {
    try {
      const token = localStorage.getItem('firebaseToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await apiClient.get(API_ENDPOINTS.prospectStages, {
        headers,
        params: { state },
      });
      const data = response.data;

      if (data?.BUYING?.stages && data?.LISTING?.stages) {
        cache.set(state, {
          BUYING: data.BUYING.stages,
          LISTING: data.LISTING.stages,
        });
        console.log(
          `[ProspectStageCache] Loaded ${state} stages from API:`,
          data.BUYING.stages.length, 'buyer,',
          data.LISTING.stages.length, 'seller',
        );
      } else {
        console.warn(
          `[ProspectStageCache] Unexpected API response shape for state ${state}, stages unavailable`,
        );
      }
    } catch (error) {
      console.warn(
        `[ProspectStageCache] Failed to load state ${state} from API:`,
        error?.message || error,
      );
    } finally {
      loadPromises.delete(state);
    }
  })();

  loadPromises.set(state, promise);
  return promise;
};

/** Whether stages have been successfully loaded for the given state (defaults to CA). */
export const areStagesLoaded = (state) => cache.has(normalizeState(state));

/**
 * Ensure stages are loaded for the given state before proceeding. Triggers a fetch
 * if the state isn't cached yet.
 */
export const ensureStagesLoaded = (state) => loadProspectStages(normalizeState(state));

/**
 * Get cached stages for (state, side). Synchronous — returns whatever is cached.
 *
 * Fallback: if the requested state isn't loaded yet, returns the CA cache (or an
 * empty array if CA itself hasn't loaded yet). Callers that want to wait for the
 * exact state should call ensureStagesLoaded first.
 */
export const getCachedStages = (side, state) => {
  const sideKey = (typeof side === 'string' && side.toUpperCase() === 'LISTING') ? 'LISTING' : 'BUYING';
  const normalized = normalizeState(state);

  const exact = cache.get(normalized);
  if (exact && Array.isArray(exact[sideKey])) {
    return exact[sideKey];
  }

  // Fallback to CA if the requested state isn't loaded yet
  const fallback = cache.get(DEFAULT_STATE);
  if (fallback && Array.isArray(fallback[sideKey])) {
    if (normalized !== DEFAULT_STATE) {
      console.warn(
        `[ProspectStageCache] State ${normalized} not loaded — falling back to ${DEFAULT_STATE}`,
      );
    }
    return fallback[sideKey];
  }
  return [];
};
